import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, AfterViewInit, Output, ViewChild, CUSTOM_ELEMENTS_SCHEMA, OnChanges, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { MapLocation } from '../../types/location';
import { watch } from '@arcgis/core/core/reactiveUtils';
import '@arcgis/map-components/components/arcgis-map'
import '@arcgis/map-components/components/arcgis-zoom'
import type { ArcgisMap } from "@arcgis/map-components/components/arcgis-map";
import Basemap from '@arcgis/core/Basemap';
import "@arcgis/map-components/components/arcgis-basemap-gallery";
import "@arcgis/map-components/components/arcgis-expand";
import "@arcgis/map-components/components/arcgis-search";
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import esriConfig from '@arcgis/core/config';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { LocalStorageService } from '../../../core/services/local-storage.service';
import "@arcgis/map-components/components/arcgis-placement";
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol.js";
import UniqueValueRenderer from "@arcgis/core/renderers/UniqueValueRenderer.js";
import { ProjectModel} from '../../../../hub_schema/hubTypes';
import { ErrorService } from '../../../../app/core/services/error.service';

@Component({
    selector: 'app-map-container',
    templateUrl: './map-container.component.html',
    styleUrls: ['./map-container.component.scss'],
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class MapContainerComponent implements OnChanges, OnDestroy, AfterViewInit {
    @Input() public mapType: string;

    public showingBasemapGallery: boolean = false;
    private basemapWatcher: IHandle;

    // #region Portfolio Map Properties

    private _hits: any[];

    public get hits(): any[] {
        return this._hits;
    }

    @Input()
    public set hits(value: any[]) {
        this._hits = value;
    }

    @ViewChild('mapWrapper')
    private mapWrapper: ElementRef;

    @ViewChild('mapDiv')
    private mapDiv: ElementRef<ArcgisMap>;

    @ViewChild('mapSearch')
    private mapSearch: ElementRef;

    @ViewChild('legend')
    private legend: ElementRef;

    // #region other map properties

    @Input()
    public project: ProjectModel;

    // #endregion

    // #endregion

    @Output()
    public mapLoaded = new EventEmitter();

    @Output()
    public mapLocationSelected = new EventEmitter<MapLocation>();

    private mapView;

    private errorService: ErrorService = inject(ErrorService);

    // #region Constructor and Initialization

    constructor(
        public router: Router,
        private eRef: ElementRef) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['hits']) {
            if(changes['hits'].currentValue !== undefined && this.mapView) {
                this.addFeaturesToMap();
            }
        }
    }

    public ngAfterViewInit(): void {
        // hack: ESRI mapView neds an explicit height set (in pixels) to render correctly
        const div = this.mapWrapper.nativeElement;
        const height = div.parentElement.parentElement.clientHeight - 55;
        div.style.height = `${height}px`;

        if (this.mapView) {
            this.killMap();
        }
    }

    // #endregion

    // #region Instance Methods
    private popupTemplate = {
        title: '{hubRecordType} ({status})',
        content: '<div class="hub-popup-container {status}">'+
            '<div><span class="text-success-800 font-weight-bold">{recordTypeName}</span>'+
                '<span class="text-muted ml-1">({status})</span>'+
            '</div><div class="project-title mb-0">'+
                '<span class="font-weight-bold">{name}</span>'+
            '</div></div>',
        overwriteActions: true,
        outFields: ['hubRecordId'],
        actions: []
    };

    private makePointGraphic(projectPoint): any {
        const point = {
            type: "point",
            longitude: projectPoint._geoloc ? projectPoint._geoloc.lng : projectPoint.longitude,
            latitude: projectPoint._geoloc ? projectPoint._geoloc.lat : projectPoint.latitude
        };

        // we have data mismatches between Algolia and GraphQL.  Since map data can come from either, we resolve it here
        const attributes = {
            hubRecordId: projectPoint.projectId,
            hubRecordType: projectPoint.recordType,
            recordTypeName: projectPoint.projectType ? projectPoint.projectType.name : projectPoint.recordType,
            status: projectPoint.status.name ? projectPoint.status.name : projectPoint.status,
            name: projectPoint.name
        };

        //Create a new Graphic point for the features
        const pointGraphic = new Graphic({
            geometry: point as any,
            attributes: attributes
        });

        return pointGraphic;
    }

    //For project view or edit mode
    private setSingleMarker(): void {
        if (!this.project.longitude || !this.project.latitude) {
            return;
        }
        //ensure no other graphics are on map
        this.clearAllGraphicsMarkers();

        let graphicsLayer = this.mapView.map.findLayerById('markers') as GraphicsLayer;

        if (!graphicsLayer) {
            graphicsLayer = new GraphicsLayer();
            graphicsLayer.id = 'markers';
        }
        this.mapView.map.add(graphicsLayer);

        const point = this.makePointGraphic(this.project);

        const simpleMarkerSymbol = {
            type: 'simple-marker',
            color: this.colors[point.attributes.status],
            outline: {
                color: this.colors[point.attributes.status],
                width: 1
            }
        };

        point.symbol = simpleMarkerSymbol;
        point.popupTemplate = this.popupTemplate;

        graphicsLayer.add(point);

        this.zoomToData();
    }

    arcgisViewReadyChange(event: CustomEvent): void {
        this.mapView = event.target as ArcgisMap;

        esriConfig.apiKey = (environment as any).arcGISOnlineApiKey as string;

        const selectedBasemapId = LocalStorageService.getItem('selectedBasemapId');
        let basemap;

        if (selectedBasemapId) {
            basemap = new Basemap({ portalItem: { id: selectedBasemapId } });
        } else {
            basemap = 'hybrid';
        }

        this.mapView.zoom = this.mapType === 'edit' ? 4 : 7
        this.mapView.constraints.minZoom = 2
        this.mapView.snapToZoom = false

        const mapElement = this.mapDiv.nativeElement
        mapElement.map.basemap = basemap

        this.basemapWatcher = watch(
            () => this.mapView.map.basemap,
            (newValue) => {
                LocalStorageService.setItem('selectedBasemapId', newValue.portalItem.id);
            }
        );

        if (this.mapType != 'edit') {
            this.mapSearch.nativeElement.remove()
        } else {
            this.addSearchBoxOnSelect()
        }

        if (this.mapType != 'portfolio') {
            this.legend.nativeElement.remove()
        }

        this.mapLoaded.emit(true);

        //On initial load sometimes the map isn't ready before the hits come back
        if(this.mapType === 'portfolio' && this.hits != undefined){
            this.addFeaturesToMap();
        }

        //Project view or edit
        if (this.mapType === 'detail' || this.mapType==='edit'){
           this.setSingleMarker();
        }

        reactiveUtils.on(
            () => this.mapView.popup,
            'trigger-action',
            (event) => {
                if (event.action.id === 'view-record') {
                    const recordId = this.mapView.popup.viewModel.selectedFeature.attributes.hubRecordId
                    this.router.navigateByUrl('/portfolio/' + recordId);
                }
            }
        );
    }

    private zoomToData() {
        let graphicsLayer = this.mapView.map.findLayerById('markers') as GraphicsLayer;
        if (graphicsLayer) {
            this.mapView.goTo(graphicsLayer.graphics).catch((e) => {
                // swallow the promise rejection.
            });
        } else {
            // no hits yet.  Show Washington, DC.
            let coordinates = [-77.1142877, 38.8825732];
            this.mapView.goTo({
                center: coordinates
            })
        }
    }

    private killMap() {
        if (this.mapSearch) {
            this.mapSearch.nativeElement.remove();
        }

        this.mapView.destroy();
    }

    ngOnDestroy() {
        if (this.basemapWatcher) {
            this.basemapWatcher.remove();
        }

        if (this.mapView) {
            this.killMap();
        }
    }

    // #endregion

    navigateWithRouter(objectID) {
        this.router.navigateByUrl('portfolio/' + objectID);
    }

    private colors = {
        Active: '#3F51B5',
        Approved: '#2196F3',
        Canceled: '#CCCCCC',
        Completed: '#4CAF50',
        Discontinued: '#F44336',
        Proposed: '#666666'
    };

    private uniqueRenderer = new UniqueValueRenderer({
            field: "status",
            defaultSymbol: new SimpleMarkerSymbol({
                size: 12,
                color: "grey",
                outline: {
                    color: "grey",
                    width: 1,
                }
            }),
            uniqueValueInfos: [{
                value: "Active",
                symbol: new SimpleMarkerSymbol({
                    size: 12,
                    color: this.colors.Active,
                    outline: {
                        color: this.colors.Active,
                        width: 1,
                        }
                    })
                },{
                value: "Canceled",
                symbol: new SimpleMarkerSymbol({
                    size: 12,
                    color: this.colors.Canceled,
                    outline: {
                        color: this.colors.Canceled,
                        width: 1,
                        }
                    })
                },{
                    value: "Completed",
                    symbol: new SimpleMarkerSymbol({
                    size: 12,
                    color: this.colors.Completed,
                    outline: {
                        color: this.colors.Completed,
                        width: 1,
                        }
                    })
                },{
                    value: "Proposed",
                    symbol: new SimpleMarkerSymbol({
                    size: 12,
                    color: this.colors.Proposed,
                    outline: {
                        color: this.colors.Proposed,
                        width: 1,
                        }
                    })
            }]
        })

    private addFeaturesToMap(): void {
        if (!this.hits) {
            return;
        }

        let graphicsFeatureLayer = this.mapView.map.findLayerById('hubFeatureLayer') as FeatureLayer;

        const viewRecordAction: any = {
            title: 'View Record',
            id: 'view-record',
            className: 'esri-icon-review'
        };

        this.popupTemplate.actions = [viewRecordAction]

        //If the layer isn't already there create and add to the map
        if (!graphicsFeatureLayer) {
            //empty feature layer
            graphicsFeatureLayer = new FeatureLayer({
                title: 'Hub Records',
                id: "hubFeatureLayer",
                objectIdField: "ObjectID",
                fields: [
                    {
                        name: "ObjectID",
                        alias: "ObjectID",
                        type: "oid"
                    },
                    {
                        name: "name",
                        alias: "name",
                        type: "string"
                    },
                    {
                        name: "status",
                        alias: "status",
                        type: "string"
                    },
                    {
                        name: "hubRecordId",
                        alias: "hubRecordId",
                        type: "integer"
                    },
                    {
                        name: "hubRecordType",
                        alias: "hubRecordType",
                        type: "string"
                    },
                    {
                        name: "recordTypeName",
                        alias: "recordTypeName",
                        type: "string"
                    }
                ],
                geometryType: "point",
                source: [], // adding an empty feature collection
                renderer: this.uniqueRenderer,
                popupTemplate: this.popupTemplate,

            })

            //add to map
            this.mapView.map.add(graphicsFeatureLayer);
        }

        let graphicsArray = [] as Array<any>

        for (let hit of this.hits) {
            if (hit.hasLocation) {
                if (!hit._geoloc.lng || !hit._geoloc.lat) {
                    continue;
                }
                const point = this.makePointGraphic(hit)

                graphicsArray.push(point)
            }
        }

        //if there are items in the graphic array call the feature layer to apply edits
        if (graphicsArray.length > 0 ){
            const addEdits = {
                addFeatures: graphicsArray
            };

            //clear existing graphics
            this.clearAllMarkers()

            //apply new edits
            graphicsFeatureLayer
            .applyEdits(addEdits)
            .then((results)=>{
                if(results.addFeatureResults.length >0){
                    if(results.addFeatureResults.length === 1){
                        //For some reason when there is just one it will stay zoomed out if it started this way
                        this.mapView.goTo({
                            center: graphicsArray,
                            zoom: 7
                            }, {duration: 2000, easing: 'ease-in-out'}).catch(e => {
                            // swallow the promise rejection.
                        });
                    } else {
                        this.mapView.goTo(graphicsArray, { duration: 2000, easing: 'ease-in-out' }).catch(e => {
                            // swallow the promise rejection.
                        });
                    }
                }
            }).catch((error) => {
                this.errorService.addError(error, true);
            });
        } else if (graphicsArray.length === 0 ){
            this.clearAllMarkers();
        }
    }

     private async clearAllMarkers(): Promise<void> {
        const graphicsFeatureLayer = this.mapView.map.findLayerById('hubFeatureLayer') as FeatureLayer;

        if (graphicsFeatureLayer) {
            try {
                const results = await graphicsFeatureLayer.queryFeatures();

                if (results.features.length > 0) {
                    const deleteEdits = {
                        deleteFeatures: results.features
                    };

                    await graphicsFeatureLayer.applyEdits(deleteEdits);
                }

            } catch (error) {
                this.errorService.addError(error, true);
                throw error; // rethrow if you want the caller to handle it
            }
        }
    }

    private clearAllGraphicsMarkers(): void {
        const markersLayer = this.mapView.map.findLayerById('markers') as GraphicsLayer;

        if (markersLayer) {
            markersLayer.removeAll();
        }
    }

    private addSearchBoxOnSelect(): void {

        this.mapSearch.nativeElement.addEventListener('arcgisSelectResult', (e: any)=>{
            const selection = new MapLocation();
            selection.address = e.detail.result.name;
            selection.latitude = e.detail.result.feature.geometry.latitude;
            selection.longitude = e.detail.result.feature.geometry.longitude;

            this.clearAllGraphicsMarkers();
            this.project.longitude = selection.longitude;
            this.project.latitude = selection.latitude;
            this.setSingleMarker();
            this.mapLocationSelected.emit(selection);
        })

    }

    @HostListener('document:click', ['$event'])
    public userClickedOutsideOfPanel(event) {
        if (!this.eRef) {
            return;
        }

        if (!this.eRef.nativeElement.contains(event.target)) {
            this.showingBasemapGallery = false;
        }
        if (event.target.classList.contains('esri-view-surface')) {
            this.showingBasemapGallery = false;
        }
    }
}
